import { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { Table, Descriptions, Typography, type TableColumnsType } from 'antd';
import Tag from './Tag';
import type { Tool } from './types';

const { Text } = Typography;

const statusOrder: Record<string, number> = {
  IMPLEMENTED: 0,
  PLANNED: 1,
  DEPRECATED: 2,
};

const typeOrder: Record<string, number> = {
  'read-only': 0,
  'secrets-access': 1,
  'cost-impact': 2,
  'admin-config': 3,
  write: 4,
};

const getTypeTagClassName = (type: string): string => {
  switch (type) {
    case 'secrets-access':
      return 'bg-[#fff2f0] dark:bg-[#2c1618] text-[#ff4d4f] dark:text-[#ff7875] border-[#ffccc7] dark:border-[#5a2829]';
    case 'write':
      return 'bg-[#fff2f0] dark:bg-[#2c1618] text-[#ff4d4f] dark:text-[#ff7875] border-[#ffccc7] dark:border-[#5a2829]';
    case 'cost-impact':
      return 'bg-[#fff7e6] dark:bg-[#2e2111] text-[#fa8c16] dark:text-[#ffa940] border-[#ffe7ba] dark:border-[#4d3819]';
    case 'admin-config':
      return 'bg-[#fff7e6] dark:bg-[#2e2111] text-[#fa8c16] dark:text-[#ffa940] border-[#ffe7ba] dark:border-[#4d3819]';
    case 'read-only':
      return 'bg-[#f6ffed] dark:bg-[#162312] text-[#52c41a] dark:text-[#73d13d] border-[#b7eb8f] dark:border-[#274916]';
    default:
      return 'bg-[#f5f5f5] dark:bg-[#2a2a2a] text-[#607d8b] dark:text-[#999999] border-[#e0e0e0] dark:border-[#434343]';
  }
};

const getStatusTagClassName = (status: string): string => {
  switch (status) {
    case 'IMPLEMENTED':
      return 'bg-[#e6f4ff] dark:bg-[#111d2c] text-[#1677ff] dark:text-[#69c0ff] border-[#91caff] dark:border-[#15395b]';
    case 'PLANNED':
      return 'bg-[#fff7e6] dark:bg-[#2e2111] text-[#fa8c16] dark:text-[#ffa940] border-[#ffe7ba] dark:border-[#4d3819]';
    case 'DEPRECATED':
      return 'bg-[#f5f5f5] dark:bg-[#2a2a2a] text-[#607d8b] dark:text-[#999999] border-[#e0e0e0] dark:border-[#434343]';
    default:
      return 'bg-[#f5f5f5] dark:bg-[#2a2a2a] text-[#607d8b] dark:text-[#999999] border-[#e0e0e0] dark:border-[#434343]';
  }
};

const ExpandedContent = ({ tool }: { tool: Tool }) => {
  const descriptionItems = [
    {
      key: 'tool_name',
      label: 'Name:',
      children: <Text className="text-sm font-normal leading-[22px] !text-theme-text">{tool.tool_name}</Text>,
    },
    {
      key: 'tool_description',
      label: 'Description:',
      children: <Text className="text-sm font-normal leading-[22px] !text-theme-text">{tool.tool_description}</Text>,
    },
  ];

  return (
    <div className="px-6 py-6">
      <Descriptions
        items={descriptionItems}
        column={2}
        bordered={false}
        className={`bg-transparent
          [&_.ant-descriptions-item-label]:text-sm
          [&_.ant-descriptions-item-label]:font-bold
          [&_.ant-descriptions-item-label]:font-sans
          [&_.ant-descriptions-item-content]:text-sm
          [&_.ant-descriptions-item-content]:font-sans`}
      />
    </div>
  );
};

interface ToolsTableProps {
  tools: Tool[];
}

const ToolsTable = ({ tools }: ToolsTableProps) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly React.Key[]>([]);

  const columns: TableColumnsType<Tool> = [
    {
      title: 'Name',
      dataIndex: 'tool_name',
      key: 'tool_name',
      sorter: (a, b) => a.tool_name.localeCompare(b.tool_name),
      render: (toolName: string) => (
        <div className="text-sm">{toolName}</div>
      ),
      className: 'px-6 py-3',
    },
    {
      title: 'Description',
      dataIndex: 'tool_description',
      key: 'tool_description',
      sorter: (a, b) => a.tool_description.localeCompare(b.tool_description),
      render: (description: string) => (
        <div className="text-sm">{description}</div>
      ),
      className: 'px-6 py-3',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: (a, b) => (typeOrder[a.type] || 999) - (typeOrder[b.type] || 999),
      render: (type: string) => (
        <Tag name={type} className={getTypeTagClassName(type)} />
      ),
      className: 'px-6 py-3 whitespace-nowrap',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => (statusOrder[a.status] || 999) - (statusOrder[b.status] || 999),
      render: (status: string) => (
        <Tag name={status} className={getStatusTagClassName(status)} />
      ),
      className: 'px-6 py-3 whitespace-nowrap',
    },
  ];

  // No color overrides here: header, row hover, borders, and expanded-row
  // backgrounds come from the consumer's AntD ConfigProvider theme, so the
  // table matches every other table in the host app.
  return (
    <div>
      <Table
        columns={columns}
        dataSource={tools.map((tool, index) => ({ ...tool, key: index }))}
        pagination={false}
        expandable={{
          expandedRowKeys: expandedRowKeys as React.Key[],
          onExpandedRowsChange: (keys) => {
            setExpandedRowKeys(keys);
          },
          expandedRowRender: (tool) => <ExpandedContent tool={tool} />,
          expandIcon: ({ expanded, onExpand, record }) => (
            <button
              onClick={(e) => onExpand(record, e)}
              className="px-3 py-4 text-center"
              type="button"
            >
              {expanded ? (
                <Minus className="w-4 h-4 text-theme-muted" />
              ) : (
                <Plus className="w-4 h-4 text-theme-muted" />
              )}
            </button>
          ),
          columnWidth: 48,
        }}
        rowClassName="cursor-pointer"
      />
    </div>
  );
};

export default ToolsTable;
